import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Select, SelectOption } from "@/components/Select";
import { Textarea } from "@/components/Textarea";
import { Tender } from "@/pages/Tenders/types";
import { Badge } from "@/components/Badge";

interface StudyStatusModalProps {
    isOpen: boolean;
    study: Tender | null;
    onClose: () => void;
    onSubmit: (targetStatusId: number, reason: string | null) => Promise<void>;
    isSubmitting?: boolean;
}

export const StudyStatusModal: React.FC<StudyStatusModalProps> = ({
    isOpen,
    study,
    onClose,
    onSubmit,
    isSubmitting = false,
}) => {
    const { t } = useTranslation("global");

    const [targetStatusId, setTargetStatusId] = useState<number | 0>(0);
    const [reason, setReason] = useState<string>("");

    useEffect(() => {
        if (study) {
            let defaultNext = 0;
            if (study.status_id === 5) {
                defaultNext = 6; // Presentado
            } else {
                if (study.status_id === 6) {
                    defaultNext = 8; // Adjudicado
                }
            }

            setTargetStatusId(defaultNext);
            setReason(study.cancellation_reason || "");
        }
    }, [study, isOpen]);

    if (!study) {
        return null;
    }

    const currentStatusId = study.status_id;

    const statusOptions: SelectOption<number>[] = [
        { value: 5, label: t("studies.status.in_study") },
        { value: 6, label: t("studies.status.submitted") },
        { value: 7, label: t("studies.status.not_submitted") },
        { value: 8, label: t("studies.status.awarded") },
        { value: 9, label: t("studies.status.not_awarded") },
    ].filter((option) => option.value !== currentStatusId);

    const handleSubmit = async (): Promise<void> => {
        if (!targetStatusId) {
            return;
        }

        await onSubmit(targetStatusId, reason.trim() || null);
    };

    const statusKeyMap: Record<number, string> = {
        5: "studies.status.in_study",
        6: "studies.status.submitted",
        7: "studies.status.not_submitted",
        8: "studies.status.awarded",
        9: "studies.status.not_awarded",
    };

    const currentStatusLabel = statusKeyMap[currentStatusId]
        ? t(statusKeyMap[currentStatusId])
        : t("tender.status.historical");

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("studies.status_modal_title")}
            maxWidth="md"
            onSubmit={handleSubmit}
            submitLabel={t("tender.confirm")}
            cancelLabel={t("common.close")}
            isSubmitting={isSubmitting}
            submitDisabled={!targetStatusId}
        >
            <div className="space-y-4">
                {Boolean(
                    study.tender?.code ||
                        (study as any).tender_code ||
                        study.code
                ) && (
                    <div className="text-center">
                        <Badge
                            value={
                                study.tender?.code ||
                                (study as any).tender_code ||
                                study.code
                            }
                        />
                    </div>
                )}

                <div className="text-center rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-dark-card font-medium text-slate-800 dark:text-zinc-200">
                    {study.title}
                </div>

                <div className="text-center rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-dark-card">
                    <p className="text-xs text-slate-500 mb-0.5">
                        {t("tender.current_status")}
                    </p>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                        {currentStatusLabel}
                    </p>
                </div>

                <Select<number>
                    label={t("tender.target_status")}
                    value={targetStatusId}
                    onChange={(val) => setTargetStatusId(Number(val))}
                    options={statusOptions}
                    full
                />

                <Textarea
                    label={t("tender.status_reason")}
                    value={reason}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setReason(e.target.value)
                    }
                    placeholder={t("tender.field_observations")}
                    resizable={true}
                    fullscreenEnabled={true}
                    rows={4}
                />
            </div>
        </Modal>
    );
};
